import React from 'react';
import { 
  Sparkles, 
  ThumbsUp, 
  TrendingUp, 
  AlertTriangle, 
  Globe, 
  Copy, 
  RefreshCw, 
  Download,
  Search
} from 'lucide-react';
import { AnimatedNumber } from './AnimatedNumber';
import { t } from '../services/i18n';
import { useLanguageUpdate } from '../src/hooks/useLanguageChange';

interface HeroScoreSectionProps {
  score: number;
  url: string;
  screenshot?: string;
  onRescan?: () => void;
  onNewScan?: () => void;
  onDownload?: () => void;
}

const getScoreColor = (score: number): string => {
  if (score >= 90) return 'text-emerald-400';
  if (score >= 70) return 'text-green-400';
  if (score >= 50) return 'text-yellow-400';
  if (score >= 30) return 'text-orange-400';
  return 'text-red-400';
};

const getScoreStatus = (score: number): { icon: React.ReactNode; message: string } => {
  if (score >= 90) {
    return {
      icon: <Sparkles className="w-8 h-8 text-yellow-300 animate-pulse" />,
      message: t('results.performanceTop') || 'Outstanding!'
    };
  }
  if (score >= 70) {
    return {
      icon: <ThumbsUp className="w-8 h-8 text-green-300" />,
      message: t('results.greatWork') || 'Great Work!'
    };
  }
  if (score >= 50) {
    return {
      icon: <TrendingUp className="w-8 h-8 text-yellow-300" />,
      message: t('results.goodProgress') || 'Good Progress'
    };
  }
  return {
    icon: <AlertTriangle className="w-8 h-8 text-orange-300" />,
    message: t('results.performanceLow') || 'Needs Improvement'
  };
};

export const HeroScoreSection: React.FC<HeroScoreSectionProps> = ({
  score,
  url,
  screenshot,
  onRescan,
  onNewScan,
  onDownload
}) => {
  // Listen for language changes to update translations
  useLanguageUpdate();
  
  const scoreColor = getScoreColor(score);
  const status = getScoreStatus(score);
  
  // Proper circle dimensions - using 100x100 viewBox for easier calculations
  const radius = 42; // Smaller radius for better proportions
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (circumference * score / 100);
  const center = 50; // Center of 100x100 viewBox

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(url);
  };

  return (
    <section className="relative overflow-hidden rounded-2xl mb-8 bg-slate-900 border border-slate-700">
      {/* Dark Background with Subtle Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        {/* Subtle Animated Orbs */}
        <div 
          className="absolute top-0 left-1/4 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl animate-pulse"
          style={{ animationDuration: '4s' }}
        ></div>
        <div 
          className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse"
          style={{ animationDuration: '4s', animationDelay: '1s' }}
        ></div>
      </div>

      <div className="relative z-10 py-12 text-center px-6">
        {/* Desktop/Laptop Mockup with Screenshot */}
        {screenshot && typeof screenshot === 'string' && screenshot.length > 100 && (
          <div className="mb-8 flex justify-center px-4">
            <div className="relative w-full max-w-5xl">
              {/* Laptop Frame */}
              <div className="relative mx-auto transform perspective-1000">
                {/* Laptop Screen */}
                <div className="relative">
                  {/* Screen Bezel Top */}
                  <div className="bg-gradient-to-b from-slate-800 to-slate-900 rounded-t-xl px-3 pt-2 pb-1 border-t border-x border-slate-600">
                    {/* Webcam */}
                    <div className="w-3 h-3 bg-slate-700 rounded-full mx-auto mb-1 border border-slate-600"></div>
                  </div>
                  
                  {/* Screen Display */}
                  <div className="bg-slate-900 px-2 py-1.5 border-x border-slate-600">
                    <div className="bg-slate-950 rounded-lg overflow-hidden border-2 border-slate-700 shadow-2xl">
                      <img 
                        src={screenshot.startsWith('data:') ? screenshot : `data:image/png;base64,${screenshot}`}
                        alt="Website preview" 
                        className="w-full h-auto object-contain block"
                        style={{ 
                          filter: 'brightness(0.95) contrast(1.05)',
                          maxHeight: '500px'
                        }}
                        onError={(e) => {
                          console.error('[HeroScoreSection] Screenshot image failed to load:', e);
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                        onLoad={() => {
                          console.log('[HeroScoreSection] Screenshot image loaded successfully');
                        }}
                      />
                    </div>
                  </div>
                  
                  {/* Screen Bezel Bottom */}
                  <div className="bg-gradient-to-b from-slate-900 to-slate-800 rounded-b-lg px-3 pt-1 pb-2 border-b border-x border-slate-600">
                    <div className="h-1 bg-slate-700 rounded-full"></div>
                  </div>
                </div>
                
                {/* Laptop Hinge */}
                <div className="h-2 bg-gradient-to-b from-slate-700 via-slate-600 to-slate-700 mx-8 rounded-b border-x border-b border-slate-600"></div>
                
                {/* Laptop Base/Keyboard Area */}
                <div className="bg-gradient-to-b from-slate-700 via-slate-800 to-slate-900 rounded-b-xl px-6 pt-3 pb-4 border border-slate-600 shadow-2xl">
                  {/* Keyboard Area */}
                  <div className="bg-slate-900/50 rounded-lg p-2 mb-2 border border-slate-700/50">
                    <div className="grid grid-cols-12 gap-1">
                      {Array.from({ length: 12 }).map((_, i) => (
                        <div key={i} className="h-2 bg-slate-700/50 rounded"></div>
                      ))}
                    </div>
                  </div>
                  {/* Trackpad */}
                  <div className="bg-slate-800/50 rounded-lg h-8 border border-slate-700/50"></div>
                </div>
                
                {/* Subtle Glow Effect */}
                <div className="absolute inset-0 bg-indigo-500/5 rounded-xl blur-2xl -z-10 transform scale-105"></div>
              </div>
            </div>
          </div>
        )}

        {/* Animated Score Circle - Properly Sized and Centered */}
        <div className="flex justify-center mb-6">
          <div className="relative w-36 h-36 md:w-44 md:h-44">
            {/* Circular Progress Ring (SVG Animation) */}
            <svg 
              className="w-full h-full transform -rotate-90"
              viewBox="0 0 100 100"
              preserveAspectRatio="xMidYMid meet"
            >
              {/* Background Circle */}
              <circle
                cx={center}
                cy={center}
                r={radius}
                stroke="currentColor"
                strokeWidth="8"
                fill="none"
                className="text-slate-700"
              />
              {/* Progress Circle (Animated) */}
              <circle
                cx={center}
                cy={center}
                r={radius}
                stroke="currentColor"
                strokeWidth="8"
                fill="none"
                strokeDasharray={circumference}
                strokeDashoffset={offset}
                className={`transition-all duration-2000 ease-out ${scoreColor}`}
                strokeLinecap="round"
                style={{
                  strokeDashoffset: offset,
                  transition: 'stroke-dashoffset 2s ease-out'
                }}
              />
            </svg>

            {/* Score Number (Animated Count-Up) - Perfectly Centered */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-center">
                <div className="text-4xl md:text-5xl font-black text-white leading-none">
                  <AnimatedNumber value={score} duration={2000} />
                </div>
                <div className="text-slate-400 text-[10px] md:text-xs font-semibold uppercase tracking-wider mt-0.5">
                  /100
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Score Status with Icon and Message - Compact */}
        <div className="flex items-center justify-center gap-2 mb-4">
          {score >= 90 && (
            <>
              <Sparkles className="w-6 h-6 text-yellow-400 animate-pulse" />
              <h1 className="text-2xl md:text-3xl font-bold text-white">{status.message}</h1>
              <Sparkles className="w-6 h-6 text-yellow-400 animate-pulse" />
            </>
          )}
          {score >= 70 && score < 90 && (
            <>
              <ThumbsUp className="w-6 h-6 text-green-400" />
              <h1 className="text-2xl md:text-3xl font-bold text-white">{status.message}</h1>
            </>
          )}
          {score >= 50 && score < 70 && (
            <>
              <TrendingUp className="w-6 h-6 text-yellow-400" />
              <h1 className="text-2xl md:text-3xl font-bold text-white">{status.message}</h1>
            </>
          )}
          {score < 50 && (
            <>
              <AlertTriangle className="w-6 h-6 text-orange-400" />
              <h1 className="text-2xl md:text-3xl font-bold text-white">{status.message}</h1>
            </>
          )}
        </div>

        {/* Website URL with Copy Button - Compact */}
        <div className="flex items-center justify-center gap-2 text-slate-300 mb-6 flex-wrap text-sm">
          <Globe className="w-4 h-4 flex-shrink-0" />
          <span className="font-mono break-all">{url}</span>
          <button 
            onClick={handleCopyUrl}
            className="p-1.5 hover:bg-slate-700 rounded-lg transition-colors flex-shrink-0"
            aria-label="Copy URL"
          >
            <Copy className="w-4 h-4" />
          </button>
        </div>

        {/* Quick Action Buttons - All Three */}
        <div className="flex items-center justify-center gap-3 flex-wrap">
          {onNewScan && (
            <button 
              onClick={onNewScan}
              className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg font-semibold transition-all flex items-center gap-2 hover:scale-105 active:scale-95 shadow-lg shadow-indigo-600/20"
            >
              <Search className="w-4 h-4" />
              {t('results.newScan') || 'New Scan'}
            </button>
          )}
          {onRescan && (
            <button 
              onClick={onRescan}
              className="px-5 py-2.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-semibold transition-all flex items-center gap-2 border border-slate-600 hover:scale-105 active:scale-95"
            >
              <RefreshCw className="w-4 h-4" />
              {t('results.rescan') || 'Rescan'}
            </button>
          )}
          {onDownload && (
            <button 
              onClick={onDownload}
              className="px-5 py-2.5 bg-slate-800 hover:bg-slate-700 text-white rounded-lg font-semibold transition-all flex items-center gap-2 border border-slate-600 hover:scale-105 active:scale-95"
            >
              <Download className="w-4 h-4" />
              {t('results.downloadPDF') || 'Download'}
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.7;
          }
        }
        .animate-pulse {
          animation: pulse 3s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
    </section>
  );
};

